import {
  Button,
  Form,
  Modal,
  Card,
  Space,
  Table,
  Typography,
  Select,
  DatePicker,
  Checkbox,
  Row,
  Col,
  message,
} from 'antd';
import {
  checkboxValidator,
  getColumns,
  getCommunityNameOptions,
  getHouseholdNumberOptions,
  getPlanId,
  getPlanOptions,
  planEffectiveTimeValidator,
} from '../helper';
import axios from 'axios';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

import type CommunityGroups from '@/types/database/communityGroups';
import type { SubscriptionPlan } from '@/types/database/subscriptionPlan';
import useService from '@/lib/hooks/useService';

import type { AddQRCodeFormValues, AuthSettingList } from '../types';
import styles from './index.module.scss';

const { Item, useForm } = Form;
const { Text, Title } = Typography;

const AuthSetting = () => {
  const [addQRCodeForm] = useForm();

  const [communityGroups] = useService<CommunityGroups>({
    collectionName: 'communityGroups',
  });
  const [subscriptionPlans] = useService<SubscriptionPlan>({
    collectionName: 'subscriptionPlan',
  });

  const [memberAuthList, setMemberAuthList] = useState<AuthSettingList[]>([]);
  const [isOpenAddQRCodeFormModal, setIsOpenAddQRCodeFormModal] =
    useState(false);
  const [selectedCommunityName, setSelectedCommunityName] = useState('');
  const [isCheckboxNowChecked, setIsCheckboxNowChecked] = useState(false);
  const [isCheckboxLaterChecked, setIsCheckboxLaterChecked] = useState(false);

  const getMemberAuthList = async () => {
    try {
      const res = await axios({
        method: 'GET',
        url: '/ezcon/api/backendEnv/proVersion/memberQrCodeList',
        headers: {
          authorization: localStorage.getItem('feathers-jwt'),
        },
      });
      setMemberAuthList(res.data);
    } catch (error) {
      message.error('會員權限資料載入失敗!');
    }
  };

  useEffect(() => {
    getMemberAuthList();
  }, []);

  const handleAddQRCodeFormValuesChange = (
    changedValues: AddQRCodeFormValues
  ) => {
    if ('communityName' in changedValues) {
      setSelectedCommunityName(changedValues.communityName);
      addQRCodeForm.resetFields(['householdNumber']);
    }
    if ('checkbox' in changedValues && 'now' in changedValues.checkbox) {
      const isNowChecked = changedValues.checkbox.now;
      addQRCodeForm.setFields([
        {
          name: ['checkbox', 'now'],
          errors: [],
        },
        {
          name: ['checkbox', 'later'],
          errors: [],
        },
      ]);
      addQRCodeForm.resetFields(['planEffectiveTime']);
      setIsCheckboxNowChecked(isNowChecked);
    }
    if ('checkbox' in changedValues && 'later' in changedValues.checkbox) {
      const isLaterChecked = changedValues.checkbox.later;
      addQRCodeForm.setFields([
        {
          name: ['checkbox', 'now'],
          errors: [],
        },
        {
          name: ['checkbox', 'later'],
          errors: [],
        },
      ]);
      setIsCheckboxLaterChecked(isLaterChecked);
    }
  };

  const closeAddQRCodeModal = () => {
    setSelectedCommunityName('');
    setIsOpenAddQRCodeFormModal(false);
  };

  const handleAddQRCodeFormSubmit = async (formValues: AddQRCodeFormValues) => {
    const {
      communityName,
      householdNumber,
      qrcodeExpiryTime,
      planEffectiveTime,
    } = formValues;

    const planIds = formValues.subscriptionPlans.map((planName) => {
      const id = getPlanId(subscriptionPlans, planName);
      return id;
    });

    const data = {
      communityName: communityName ?? null,
      householdNumber: householdNumber ?? null,
      subscriptionPlans: planIds,
      qrcodeExpiryTime: moment(qrcodeExpiryTime).endOf('day').valueOf(),
      planEffectiveTime: moment(planEffectiveTime).startOf('day').valueOf(),
    };

    try {
      await axios({
        method: 'POST',
        url: '/ezcon/api/backendEnv/proVersion/user/qrCode/normalMember',
        headers: {
          authorization: localStorage.getItem('feathers-jwt'),
        },
        data,
      });
      await getMemberAuthList();
      setIsOpenAddQRCodeFormModal(false);
      message.success('新增QR Code成功!');
    } catch (error) {
      message.error('新增QR Code失敗!');
    }
  };

  const communityNameOptions = getCommunityNameOptions(communityGroups);
  const householdNumberOptions = getHouseholdNumberOptions(
    communityGroups,
    selectedCommunityName
  );

  const planOptions = getPlanOptions(subscriptionPlans, selectedCommunityName);
  const columns = getColumns(getMemberAuthList);

  return (
    <>
      <Space direction="vertical" className={styles.widthFull}>
        <Button onClick={() => setIsOpenAddQRCodeFormModal(true)}>
          新增開通QR Code
        </Button>
        <Card>
          <Title level={5}>權限開通QR Code</Title>
          <Table
            bordered
            rowKey="_id"
            columns={columns}
            dataSource={memberAuthList}
            scroll={{ x: 'max-content' }}
          />
        </Card>
      </Space>
      <Modal
        open={isOpenAddQRCodeFormModal}
        title="新增開通QR Code"
        centered
        destroyOnClose
        footer={null}
        onCancel={closeAddQRCodeModal}
      >
        <Form
          form={addQRCodeForm}
          layout="vertical"
          preserve={false}
          onFinish={handleAddQRCodeFormSubmit}
          onValuesChange={handleAddQRCodeFormValuesChange}
        >
          <Item label={<Text type="secondary">社區</Text>} name="communityName">
            <Select
              placeholder="請選擇社區"
              options={communityNameOptions}
              allowClear
            />
          </Item>
          <Item
            label={<Text type="secondary">戶號</Text>}
            name="householdNumber"
          >
            <Select
              placeholder="請選擇戶號"
              options={householdNumberOptions}
              allowClear
            />
          </Item>
          <Item
            label={<Text type="secondary">開通方案</Text>}
            name="subscriptionPlans"
            rules={[{ required: true, message: '請選擇開通方案' }]}
          >
            <Select
              placeholder="請選擇開通方案"
              options={planOptions}
              mode="multiple"
            />
          </Item>
          <Item
            label={<Text type="secondary">QR Code有效截止時間</Text>}
            name="qrcodeExpiryTime"
            rules={[{ required: true, message: '請選擇Code有效截止時間' }]}
          >
            <DatePicker
              className={styles.widthFull}
              placeholder="請選擇QR Code有效截止時間"
              disabledDate={(current) => current < moment().endOf('day')}
            />
          </Item>
          <Item label={<Text type="secondary">方案生效時間</Text>} required>
            <Row>
              <Col span={24}>
                <Item
                  name={['checkbox', 'now']}
                  valuePropName="checked"
                  rules={[
                    {
                      validator: () => checkboxValidator(addQRCodeForm),
                    },
                  ]}
                >
                  <Checkbox value="now" disabled={isCheckboxLaterChecked}>
                    掃描後立即生效
                  </Checkbox>
                </Item>
              </Col>
              <Col span={24}>
                <Item
                  name={['checkbox', 'later']}
                  valuePropName="checked"
                  rules={[
                    {
                      validator: () => checkboxValidator(addQRCodeForm),
                    },
                  ]}
                >
                  <Checkbox value="later" disabled={isCheckboxNowChecked}>
                    固定時間生效
                  </Checkbox>
                </Item>
              </Col>
              <Col span={24}>
                <Item
                  name="planEffectiveTime"
                  rules={[
                    {
                      validator: (_rules, value) =>
                        planEffectiveTimeValidator(value, addQRCodeForm),
                    },
                  ]}
                >
                  <DatePicker
                    className={styles.widthFull}
                    placeholder="請選擇方案生效時間"
                    disabled={isCheckboxNowChecked}
                    disabledDate={(current) => current < moment().endOf('day')}
                  />
                </Item>
              </Col>
            </Row>
          </Item>
          <Item>
            <Space className={styles.footerButtonsContainer}>
              <Button onClick={closeAddQRCodeModal}>取消</Button>
              <Button type="primary" htmlType="submit">
                確定
              </Button>
            </Space>
          </Item>
        </Form>
      </Modal>
    </>
  );
};

export default AuthSetting;
